import React, {useState, useMemo, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
// local
import {Modal} from "components/Modal";
import {Preloader} from "components/Preloader";
import {CustomCounter} from "components/CustomCounter";
import {
  IconStandPhone,
  IconMailLetter,
  IconLicense,
  IconTags,
  IconInbox,
  IconBriefcase,
} from "components/icons/index";
import MultipleSelect from "components/selects/MultipleSelect";
import {availableInArray} from "constants/select.consts";
import CustomRadioSwitch from "components/CustomRadioSwitch";
import {InitialsBadge} from "components/InitialsBadge";
import {getFullName} from "helpers/helper";
import {Tag} from "components/Tag";
import {ProjectLabel} from "components/ProjectLabel";
import EmptyData from "components/EmptyData";
import {Info} from "components/Info";
import {REQUESTS} from "constants/routes.const";
import {color} from "constants/color.consts";

export const ModalContactMember = ({
  contactId,
  contactUserInfo,
  getData,
  onClose,
  organizationName,
  isActive,
  currentUser,
  handleToggle,
  handleRegions,
  addClass,
}) => {
  const {t} = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState(null);
  const [myContactToggle, setMyContactToggle] = useState(false);
  const hasContactInfo = useMemo(() => !!contact, [contact]);

  const user = useMemo(() => contact?.User, [contact]);
  const contactFullName = useMemo(
    () => getFullName(user?.firstName, user?.lastName),
    [user]
  );
  const contactEmail = useMemo(() => contact?.email ?? user?.email, [contact, user]);
  const contactPhone = useMemo(
    () => contact?.phone ?? contact?.mobilePhone ?? user?.mobilePhone,
    [contact, user]
  );

  const regions = useMemo(() => contact?.availableIn, [contact]);
  const hasRegions = useMemo(() => !!regions?.length, [regions]);
  const regionsCount = useMemo(() => regions?.length, [regions]);
  const regionsString = useMemo(
    () =>
      regionsCount > 2
        ? ` (${regions
            .reduce((str, region) => str.concat(t(region), ", "), "")
            .slice(0, -2)})`
        : "",
    [regions, regionsCount, t]
  );
  const regionTooltip = useMemo(
    () =>
      regionsCount
        ? `${t("CONTACT_MEMBER_MODAL_HAVE_REGIONS_TOOLTIP").format(
            contactFullName,
            organizationName
          )}${regionsString}`
        : "CONTACT_MEMBER_MODAL_NO_REGIONS_TOOLTIP",
    [regionsCount, t, organizationName, regionsString, contactFullName]
  );
  const [manualRegions, setManualRegions] = useState(null);

  const contactUsers = useMemo(
    () => contact?.OurOrganizationUsers?.filter((user) => user.id !== currentUser.id),
    [contact?.OurOrganizationUsers, currentUser]
  );
  const hasContactUsers = useMemo(() => !!contactUsers?.length, [contactUsers]);
  const contactUsersCount = useMemo(
    () => (hasContactUsers ? contactUsers.length : 0),
    [hasContactUsers, contactUsers]
  );
  const maxContacts = 4;

  const RegionTag = (props) => <Tag color={color.green} bold miniSelectView {...props} />;

  const regionsTags = (
    <React.Fragment>
      {hasRegions ? (
        <React.Fragment>
          <RegionTag text={t(regions[0])} addClass="fz-12 mr-1" />
          {regionsCount > 1 && <RegionTag text={t(regions[1])} addClass="fz-12 mr-1" />}
        </React.Fragment>
      ) : (
        <RegionTag
          text={t("CONTACT_MEMBER_MODAL_NO_REGION")}
          addClass="fz-12 mr-1 __italic"
          noBackground
        />
      )}
      {regionsCount > 2 && (
        <CustomCounter count={regionsCount - 2} addClass="ml-0 mr-1" />
      )}
      <Info tooltip={regionTooltip} centerText />
    </React.Fragment>
  );

  useEffect(() => {
    setLoading(!hasContactInfo);
    getData(contactId)
      .then((data) => {
        setContact({...data, User: contactUserInfo});
        setMyContactToggle(
          !!data.OurOrganizationUsers?.find((user) => user?.id === currentUser?.id)
        );
        setManualRegions(data.handledAvailableIn);
      })
      .finally(() => setLoading(false));
  }, [contactId, contactUserInfo, getData, currentUser, hasContactInfo]);

  return (
    <Modal
      width={800}
      rounded
      noTitleIcon
      tightTitle
      whiteTitle
      noCenterContent
      onClose={() => {
        setContact(null);
        onClose();
      }}
    >
      {loading ? (
        <Preloader addClass="preloader500" />
      ) : (
        <div className="contact-member-modal__content">
          <div className="contact-member-modal__contact-info">
            <InitialsBadge firstName={user?.firstName} lastName={user?.lastName} huge />
            <div>
              <div className="contact-member-modal__contact-info__item fz-20 __bold">
                {contactFullName}
              </div>
              {contactEmail && (
                <div className="contact-member-modal__contact-info__item email">
                  <IconMailLetter />
                  <span>{contactEmail}</span>
                </div>
              )}
              {contactPhone && (
                <div className="contact-member-modal__contact-info__item">
                  <IconStandPhone />
                  <span>{contactPhone}</span>
                </div>
              )}
            </div>
            <div>
              {contact.position && (
                <div className="contact-member-modal__contact-info__item">
                  <IconLicense />
                  <span>{contact.position}</span>
                </div>
              )}
              {contact.workKinds && (
                <div className="contact-member-modal__contact-info__item">
                  <IconTags />
                  <span>{contact.workKinds}</span>
                </div>
              )}
              <div className="contact-member-modal__contact-info__item">
                <IconInbox />
                {regionsTags}
              </div>
            </div>
          </div>
          {!isActive && (
            <React.Fragment>
              <div className="contact-member-modal__my-contact-row">
                <CustomRadioSwitch
                  label={t("CONTACT_MEMBER_MODAL_CONTACT_TOGGLE_LABEL")}
                  checked={myContactToggle}
                  small
                  uppercase
                  bold
                  labelOffset={1}
                  addWrapperClass="mr-2"
                  isActive={(status) => {
                    setMyContactToggle(status);
                    handleToggle(contact.User.id, status);
                  }}
                />

                {myContactToggle && (
                  <MultipleSelect
                    title="CONTACT_MEMBER_MODAL_CONTACT_REGIONS_BUTTON"
                    leftLabel="CONTACT_MEMBER_MODAL_CONTACT_REGIONS_LABEL"
                    list={availableInArray}
                    prefixSelections
                    tiny
                    uppercaseLabel
                    uppercaseButton
                    boldLabel
                    color="green"
                    titleInButton
                    value={manualRegions}
                    onChange={(handledAvailableIn) => {
                      setManualRegions(handledAvailableIn);
                      handleRegions(contact.UserId, {handledAvailableIn});
                    }}
                    addClass="mr-0"
                  />
                )}
                <Info tooltip="CONTACT_MEMBER_MODAL_CONTACT_REGIONS_TOOLTIP" centerText />
                {hasContactUsers && (
                  <div className="df-row-center ml-4 hght-20">
                    <span className="fz-10 __bold __uppercase mr-2">
                      {t("CONTACT_MEMBER_MODAL_CONTACT_CONTACTS_LABEL")}
                    </span>
                    {contactUsers?.slice(0, maxContacts).map((member) => (
                      <InitialsBadge
                        key={member?.id}
                        firstName={member.firstName}
                        lastName={member.lastName}
                        needTooltip
                        customTooltip={`${member.email ?? ""}`}
                        addClass="mr-1"
                      />
                    ))}

                    {hasContactUsers && contactUsersCount > maxContacts && (
                      <span className="fz-10">{`+${
                        contactUsersCount - maxContacts
                      }`}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="contact-member-modal__rfps-title">
                <IconBriefcase />
                {t("CONTACT_MEMBER_MODAL_RFPS_TITLE")}
              </div>
              <div className="contact-member-modal__rfps-list">
                {!!contact?.rfts?.length ? (
                  contact?.rfts?.map((rfp, i) => (
                    <div
                      key={i}
                      className="contact-member-modal__rfps-item"
                      onClick={() => navigate(`${REQUESTS}/${rfp.id}`)}
                    >
                      <div className="contact-member-modal__rfps-item__name">
                        {rfp.name}
                      </div>
                      <ProjectLabel
                        name={rfp.projectName}
                        code={`${t(rfp.projectAvailableIn?.[0])}${
                          rfp.projectAvailableIn?.length > 1
                            ? ` +${rfp.projectAvailableIn?.length - 1}`
                            : ""
                        }`}
                        color="green"
                        codeRounded
                        mediumText
                        addClass="ml-2 __nonecase __normal"
                      />
                    </div>
                  ))
                ) : (
                  <EmptyData addClass="pb-4" />
                )}
              </div>
            </React.Fragment>
          )}
        </div>
      )}
    </Modal>
  );
};
